import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreorderSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    if (!form.name || !form.email || !form.phone || !form.address || !form.city || !form.postal_code) {
      setErrorMsg("Wypełnij wszystkie pola.");
      setStatus("error");
      return;
    }

    try {
      await axios.post(`${API}/preorder`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", address: "", city: "", postal_code: "" });
    } catch (err) {
      setErrorMsg("Coś poszło nie tak. Spróbuj ponownie.");
      setStatus("error");
    }
  };

  return (
    <section
      id="preorder"
      data-testid="preorder-section"
      className="relative py-24 md:py-32"
    >
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#EB0023] to-transparent" />

      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2
            className="font-['Oswald'] text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4 text-[#EB0023]"
            data-testid="preorder-section-title"
          >
            Zamów preorder
          </h2>
          <p className="font-['Manrope'] text-[#A0B0C4] text-base md:text-lg max-w-2xl mx-auto">
            Pierwszy album artysty Pueblo — „Preludium" zawierający 7 unikatowych utworów możliwy do zamówienia w preorderze za 29zł. Album dostępny w limitowanej ilości oraz ponumerowany.
          </p>
        </motion.div>

        {status === "success" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
            data-testid="preorder-success-message"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#EB0023]/20 mb-6">
              <Check className="w-8 h-8 text-[#EB0023]" />
            </div>
            <h3 className="font-['Oswald'] text-2xl uppercase tracking-tighter mb-3">
              Zamówienie złożone
            </h3>
            <p className="font-['Manrope'] text-[#A0B0C4] max-w-md mx-auto">
              Na podany adres e-mail wysłaliśmy numer telefonu do przelewu BLIK oraz instrukcje płatności. Dzięki za wsparcie!
            </p>
            <p className="font-['Manrope'] text-xs text-[#32558B] mt-4 max-w-md mx-auto">
              Nie widzisz maila? Sprawdź folder spam / oferty.
            </p>
          </motion.div>
        ) : (
          <motion.form
            data-testid="preorder-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="space-y-8 bg-[#192D48]/20 border border-[#32558B]/20 p-6 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">
                  Imię i nazwisko
                </label>
                <input
                  data-testid="preorder-input-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jan Kowalski"
                  className="form-input-dark"
                  required
                />
              </div>
              <div>
                <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">
                  Adres e-mail
                </label>
                <input
                  data-testid="preorder-input-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="jan@email.com"
                  className="form-input-dark"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">
                  Numer telefonu
                </label>
                <input
                  data-testid="preorder-input-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+48 123 456 789"
                  className="form-input-dark"
                  required
                />
              </div>
              <div>
                <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">
                  Adres
                </label>
                <input
                  data-testid="preorder-input-address"
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="ul. Przykładowa 12/3"
                  className="form-input-dark"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">
                  Miasto
                </label>
                <input
                  data-testid="preorder-input-city"
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="Poznań"
                  className="form-input-dark"
                  required
                />
              </div>
              <div>
                <label className="font-['Manrope'] text-xs uppercase tracking-widest text-[#A0B0C4] mb-2 block">
                  Kod pocztowy
                </label>
                <input
                  data-testid="preorder-input-postal"
                  type="text"
                  name="postal_code"
                  value={form.postal_code}
                  onChange={handleChange}
                  placeholder="60-001"
                  className="form-input-dark"
                  required
                />
              </div>
            </div>

            <div className="bg-[#192D48]/40 border border-[#32558B]/20 p-4">
              <p className="font-['Manrope'] text-sm text-[#A0B0C4]">
                <span className="text-[#EB0023] font-semibold">Płatność BLIK</span> — po złożeniu zamówienia na podany adres e-mail zostanie wysłany numer telefonu, na który należy wykonać przelew BLIK. Po zaksięgowaniu płatności album zostanie wysłany na podany adres.
              </p>
              <p className="font-['Manrope'] text-xs text-[#32558B] mt-2">
                Sprawdź folder spam/oferty, jeśli nie widzisz maila w skrzynce odbiorczej.
              </p>
            </div>

            {errorMsg && (
              <p data-testid="preorder-error-message" className="text-[#EB0023] font-['Manrope'] text-sm">
                {errorMsg}
              </p>
            )}

            <motion.button
              data-testid="preorder-submit-button"
              type="submit"
              disabled={status === "loading"}
              className="w-full bg-[#EB0023] text-white font-['Oswald'] text-xl uppercase tracking-wider py-5 hover:bg-white hover:text-black transition-all duration-300 disabled:opacity-50 cta-pulse flex items-center justify-center gap-3"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Wysyłanie...
                </>
              ) : (
                "Kup teraz"
              )}
            </motion.button>
          </motion.form>
        )}
      </div>
    </section>
  );
}
